import { useContext } from "react";
import { Box, Container, Grid } from "@mui/material";
import { ButtonBase, FormControlBase, FormSectionTitle, TextFieldBase } from "../../../styles";
import { format, subDays } from "date-fns";
import { useFormik } from "formik";
import * as Yup from "yup";

import { PedidosContext } from "../../../../../Context/PedidosContext";
import api from "../../../../../Api/apiAxios";

const validationSchema = Yup.object({
  valorNegociado: Yup.number().required("O valor negociado é obrigatório"),
  periodoInicialNegociacao: Yup.date()
    .required("O período inicial da negociação é obrigatório")
    .typeError("Data inválida")
    .min(subDays(new Date(), 1), "O período inicial não pode ser anterior à data atual"),
  periodoFinalNegociacao: Yup.date()
    .required("O período final da negociação é obrigatório")
    .typeError("Data inválida")
    .when("periodoInicialNegociacao", (periodoInicialNegociacao, schema) =>
      periodoInicialNegociacao
        ? schema.min(periodoInicialNegociacao, "O período final deve ser uma data posterior ao período inicial")
        : schema
    ),
  descricaoMotivo: Yup.string().required("O motivo é obrigatório").min(3, "O motivo deve ter pelo menos 3 caracteres"),
});

function overlaps(aStart, aEnd, bStart, bEnd) {
  const A1 = new Date(aStart).getTime();
  const A2 = new Date(aEnd).getTime();
  const B1 = new Date(bStart).getTime();
  const B2 = new Date(bEnd).getTime();

  return A1 <= B2 && A2 >= B1;
}

export function ModalNegociacao({ contratoHistoricoId }) {
  const {
    productVariant,
    fetchNegociacoesAnteriores,
    setModalNegociacao,
    modalNegociacao,
    setNegociacoesAnteriores,
  } = useContext(PedidosContext);

  const negociacaoAtualId = Number(modalNegociacao?.negociacao?.id ?? modalNegociacao?.negociacao?.Id ?? 0);

  const formik = useFormik({
    initialValues: {
      valorNegociado: modalNegociacao?.negociacao?.valorNegociado ?? modalNegociacao?.negociacao?.ValorNegociado ?? "",
      periodoInicialNegociacao: modalNegociacao?.negociacao?.periodoInicial
        ? format(new Date(modalNegociacao.negociacao.periodoInicial), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
      periodoFinalNegociacao: modalNegociacao?.negociacao?.periodoFinal
        ? format(new Date(modalNegociacao.negociacao.periodoFinal), "yyyy-MM-dd")
        : "",
      descricaoMotivo: modalNegociacao?.negociacao?.descricaoMotivo ?? modalNegociacao?.negociacao?.DescricaoMotivo ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (!contratoHistoricoId) {
          alert("Histórico do contrato inválido para negociação.");
          return;
        }

        setModalNegociacao({ show: false, negociacao: {} });

        const regNegociacao = {
          Id: negociacaoAtualId,
          IdContrato: Number(contratoHistoricoId),
          PeriodoInicial: values.periodoInicialNegociacao,
          PeriodoFinal: values.periodoFinalNegociacao,
          ValorNegociado: Number(values.valorNegociado),
          DescricaoMotivo: values.descricaoMotivo,
          TipoSistema: productVariant,
        };


        const negociacoesExistentes = (await fetchNegociacoesAnteriores(contratoHistoricoId)) || [];

        if (regNegociacao.Id <= 0) {
          const conflito = negociacoesExistentes
            .filter((n) => Number(n.id ?? n.Id ?? 0) !== regNegociacao.Id)
            .some((n) =>
              overlaps(
                n.periodoInicial ?? n.PeriodoInicial,
                n.periodoFinal ?? n.PeriodoFinal,
                values.periodoInicialNegociacao,
                values.periodoFinalNegociacao
              )
            );

          if (conflito) {
            alert("Já existe uma negociação cadastrada para esse período.");
            return;
          }
        }

        if (regNegociacao.Id > 0) {
          await api.put("/NegociacaoContrato", regNegociacao, {
            headers: { "Content-Type": "application/json" },
          });
        } else {
          await api.post("/NegociacaoContrato", regNegociacao, {
            headers: { "Content-Type": "application/json" },
          });
        }

        const negociacoesAtualizadas = (await fetchNegociacoesAnteriores(contratoHistoricoId)) || [];
        const ordenadas = [...negociacoesAtualizadas].sort((a, b) => new Date(b.periodoInicial) - new Date(a.periodoInicial));
        setNegociacoesAnteriores(ordenadas);
      } catch (e) {
        console.error(e);
        alert("Erro ao salvar negociação.");
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box
        sx={{
          backgroundColor: "white",
          py: 3,
          px: 1,
          my: 2,
          borderRadius: "30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          width: "70%",
          maxWidth: "65rem",
          alignSelf: "center",
          position: "fixed",
          top: 100,
          zIndex: 1000,
          maxHeight: "80vh",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
        }}
      >
        <FormSectionTitle label={negociacaoAtualId ? "Editar Negociação" : "Adicionar Negociação"} />

        <Container sx={{ my: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControlBase>
                <TextFieldBase
                  type="date"
                  label="Período Inicial"
                  name="periodoInicialNegociacao"
                  width="100%"
                  value={formik.values.periodoInicialNegociacao}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.periodoInicialNegociacao && Boolean(formik.errors.periodoInicialNegociacao)}
                  helperText={formik.touched.periodoInicialNegociacao && formik.errors.periodoInicialNegociacao}
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlBase>
                <TextFieldBase
                  type="date"
                  label="Período Final"
                  name="periodoFinalNegociacao"
                  width="100%"
                  value={formik.values.periodoFinalNegociacao}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.periodoFinalNegociacao && Boolean(formik.errors.periodoFinalNegociacao)}
                  helperText={formik.touched.periodoFinalNegociacao && formik.errors.periodoFinalNegociacao}
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlBase>
                <TextFieldBase
                  label="Valor Negociado (R$)"
                  name="valorNegociado"
                  width="100%"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.valorNegociado && Boolean(formik.errors.valorNegociado)}
                  helperText={formik.touched.valorNegociado && formik.errors.valorNegociado}
                  placeholder="0.00"
                  type="number"
                  value={formik.values.valorNegociado}
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={12}>
              <FormControlBase>
                <TextFieldBase
                  label="Motivo"
                  name="descricaoMotivo"
                  width="100%"
                  value={formik.values.descricaoMotivo}
                  onChange={formik.handleChange}
                  multiline
                  rows={4}
                  onBlur={formik.handleBlur}
                  error={formik.touched.descricaoMotivo && Boolean(formik.errors.descricaoMotivo)}
                  helperText={formik.touched.descricaoMotivo && formik.errors.descricaoMotivo}
                />
              </FormControlBase>
            </Grid>
          </Grid>
        </Container>

        <Box sx={{ display: "flex", width: "95%", justifyContent: "flex-end", gap: 2, my: 1 }}>
          <ButtonBase variant="outlined" onClick={() => setModalNegociacao({ show: false, negociacao: {} })}>
            Cancelar
          </ButtonBase>
          <ButtonBase onClick={formik.submitForm} variant="contained" disabled={formik.isSubmitting}>
            Salvar
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}
